import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import type {
  AccountOption,
  AssetOption,
  InternalAccountSectionCardType,
  InternalTransferFormValues,
  InternalTransferInitialContext,
} from '@/features/internal-transfer';
import type {
  AccontTypeData,
  AccountType,
  RegularAccount,
} from '@/entities/account';
import type { UseFormReturn } from 'react-hook-form';

interface Args {
  form: UseFormReturn<InternalTransferFormValues>;
  accountOptions: AccountOption[];
  accountsFrom: InternalAccountSectionCardType[];
  accountsTo: InternalAccountSectionCardType[];
  setSelectedAssetFrom?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAssetTo?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAccountFrom?: Dispatch<SetStateAction<AccountOption | undefined>>;
  setSelectedAccountTo?: Dispatch<SetStateAction<AccountOption | undefined>>;
  ewallet: AccontTypeData<RegularAccount> | null;
  initialContext?: InternalTransferInitialContext;
  isInitFormData: boolean;
}

export const useInternalTransferInitForm = ({
  form,
  ewallet,
  accountOptions,
  accountsTo,
  accountsFrom,
  initialContext,
  isInitFormData,
  setSelectedAssetFrom,
  setSelectedAccountTo,
  setSelectedAccountFrom,
  setSelectedAssetTo,
}: Args) => {
  const internalTransferFormInitedRef = useRef<boolean>(false);

  const getAccountByTypeAndAccNum = useCallback(
    (type: AccountType, accNum: string) => {
      return accountOptions.find(
        acc => acc.type === type && acc.accNum === accNum
      );
    },
    [accountOptions]
  );

  const getAccountByType = useCallback(
    (type: AccountType) => {
      return accountOptions.find(acc => acc.type === type);
    },
    [accountOptions]
  );

  const findAsset = useCallback(
    (
      accountType: AccountType,
      accountNumber: string,
      assetCode: string,
      direction: 'from' | 'to'
    ) => {
      const group = (direction === 'from' ? accountsFrom : accountsTo).find(
        a => a.type === accountType
      );
      const account = group?.data?.accounts?.find(
        acc => acc.accNum === accountNumber
      );
      const asset = account?.assets?.find(a => a.code === assetCode);

      if (accountType === 'invest' && asset) {
        if (account && 'marginIndicators' in account) {
          return {
            ...asset,
            ...account.marginIndicators,
          };
        }
      }

      return asset;
    },
    [accountsTo, accountsFrom]
  );

  useEffect(() => {
    const mainAccount = getAccountByType('ewallet');

    if (internalTransferFormInitedRef.current) {
      return;
    }

    if (!initialContext) {
      if (
        mainAccount &&
        !isInitFormData &&
        !form.formState.isDirty &&
        !internalTransferFormInitedRef.current
      ) {
        internalTransferFormInitedRef.current = true;
        if (setSelectedAccountFrom) {
          setSelectedAccountFrom(mainAccount);
        }
        form.setValue('accNumFrom', mainAccount.accNum, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
      return;
    }

    internalTransferFormInitedRef.current = true;

    const { accountType, accountNumber, assetCode, direction } = initialContext;

    const resolvedDirection =
      direction ?? (accountType === 'ewallet' ? 'from' : 'to');

    const isFrom = resolvedDirection === 'from';

    if (isFrom) {
      const account =
        accountType === 'ewallet'
          ? mainAccount
          : getAccountByTypeAndAccNum(accountType, accountNumber);

      if (setSelectedAccountFrom && account) {
        setSelectedAccountFrom(account);
      }

      form.setValue(
        'accNumFrom',
        accountType === 'ewallet' ? (mainAccount?.accNum ?? '') : accountNumber,
        {
          shouldValidate: false,
          shouldDirty: true,
        }
      );

      const asset =
        accountType === 'ewallet'
          ? ewallet?.accounts[0]?.assets?.find(
              asset => asset.code === assetCode
            )
          : findAsset(accountType, accountNumber, assetCode, 'from');

      if (setSelectedAssetFrom) {
        setSelectedAssetFrom(
          asset
            ? {
                ...asset,
                available:
                  'equity' in asset
                    ? (asset?.equity ?? 0)
                    : (asset.available ?? 0),
              }
            : undefined
        );
      }

      form.setValue('assetFrom', assetCode, {
        shouldValidate: false,
        shouldDirty: true,
      });
    } else {
      const account = getAccountByTypeAndAccNum(accountType, accountNumber);

      if (setSelectedAccountTo && account) {
        setSelectedAccountTo(account);
      }

      form.setValue('accNumTo', accountNumber, {
        shouldValidate: false,
        shouldDirty: true,
      });

      const asset = findAsset(accountType, accountNumber, assetCode, 'to');

      if (setSelectedAssetTo) {
        setSelectedAssetTo(
          asset
            ? {
                ...asset,
                available: 0,
              }
            : undefined
        );
      }

      form.setValue('assetTo', assetCode, {
        shouldValidate: false,
        shouldDirty: true,
      });

      if (mainAccount) {
        if (setSelectedAccountFrom) {
          setSelectedAccountFrom(mainAccount);
        }

        form.setValue('accNumFrom', mainAccount.accNum, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    }
  }, [
    initialContext,
    form,
    ewallet,
    accountsTo,
    internalTransferFormInitedRef.current,
    getAccountByType,
    getAccountByTypeAndAccNum,
    findAsset,
    setSelectedAccountFrom,
    setSelectedAccountTo,
    setSelectedAssetFrom,
    setSelectedAssetTo,
  ]);
};
